import { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid-pro";
import { HalftimeDashboardRow } from "./types";
import {
  getCrashColor,
  getHitAheadColor,
  getPaintTouchColor,
  getPotentialAssistColor,
} from "./utils";
import ShotProfileCell from "./ShotProfileCell";
import ShotHeader from "./ShotHeader";
import StatCell from "./StatCell";

export const getDashboardColumns = (): GridColDef<HalftimeDashboardRow>[] => [
  {
    field: "name",
    headerName: "",
    width: 150,
    sortable: false,
  },
  {
    field: "totalPossessions",
    headerName: "Poss",
    width: 60,
    type: "number",
  },
  // Rebounding
  {
    field: "threePtCrashes",
    headerName: "3P Crash%",
    width: 110,
    renderCell: ({ row }) => {
      if (row.threePtCrashOpportunities === null || row.threePtCrashOpportunities === 0) return "-";
      const value = row.threePtCrashes ?? 0;
      const pct = Math.round((value / row.threePtCrashOpportunities) * 100);
      return (
        <StatCell
          topLabel={`${pct}%`}
          bottomLabel={`${value}/${row.threePtCrashOpportunities}`}
          topBgColor={getCrashColor(pct, row.positionCategory)}
        />
      );
    },
  },
  // Passing
  {
    field: "hitAheadPasses",
    headerName: "Hit Ahead",
    width: 90,
    type: "number",
    renderCell: ({ row }) => {
      const value = row.hitAheadPasses ?? 0;
      if (!value) return "-";
      if (!row.isTeam) return value;
      const totalPoss = row.totalPossessions ?? 0;
      const rateNum = totalPoss ? value / totalPoss * 100 : 0;
      return (
        <StatCell
          topLabel={`${value}`}
          bottomLabel={`${Math.round(rateNum)}%`}
          bottomBgColor={totalPoss ? getHitAheadColor(rateNum) : undefined}
        />
      );
    },
  },
  {
    field: "paintTouches",
    headerName: "Paint Touch",
    width: 100,
    type: "number",
    renderCell: ({ row }) => {
      const value = row.paintTouches ?? 0;
      if (!value) return "-";
      if (!row.isTeam) return value;
      const totalPoss = row.totalPossessions ?? 0;
      const rateNum = totalPoss ? value / totalPoss * 100 : 0;
      return (
        <StatCell
          topLabel={`${value}`}
          bottomLabel={`${Math.round(rateNum)}%`}
          bottomBgColor={totalPoss ? getPaintTouchColor(rateNum) : undefined}
        />
      );
    },
  },
  {
    field: "potentialAssists",
    headerName: "Pot. AST",
    width: 80,
    type: "number",
    renderCell: ({ row }) => {
      const value = row.potentialAssists ?? 0;
      if (!value) return "-";
      if (!row.isTeam) return value;
      const totalFga = row.totalFga ?? 0;
      const rateNum = totalFga ? value / totalFga * 100 : 0;
      return (
        <StatCell
          topLabel={`${value}`}
          bottomLabel={`${Math.round(rateNum)}%`}
          bottomBgColor={totalFga ? getPotentialAssistColor(rateNum) : undefined}
        />
      );
    },
  },
  // Shot Profile
  {
    field: "rim",
    headerName: "Rim",
    width: 160,
    renderHeader: () => <ShotHeader label="Rim" />,
    renderCell: ({ row }) => (
      <ShotProfileCell
        openMade={row.rimOpenMade}
        openAtt={row.rimOpenAtt}
        lightMade={row.rimLightMade}
        lightAtt={row.rimLightAtt}
        heavyMade={row.rimHeavyMade}
        heavyAtt={row.rimHeavyAtt}
        shotType="rim"
      />
    ),
  },
  {
    field: "nonRimTwo",
    headerName: "Non-Rim 2",
    width: 160,
    renderHeader: () => <ShotHeader label="Non-Rim 2" />,
    renderCell: ({ row }) => (
      <ShotProfileCell
        openMade={row.nonRimTwoOpenMade}
        openAtt={row.nonRimTwoOpenAtt}
        lightMade={row.nonRimTwoLightMade}
        lightAtt={row.nonRimTwoLightAtt}
        heavyMade={row.nonRimTwoHeavyMade}
        heavyAtt={row.nonRimTwoHeavyAtt}
        shotType="nonRimTwo"
      />
    ),
  },
  {
    field: "threeNoncs",
    headerName: "Pull-Up 3P",
    width: 160,
    renderHeader: () => <ShotHeader label="Pull-Up 3" />,
    renderCell: ({ row }) => (
      <ShotProfileCell
        openMade={row.threeNoncsOpenMade}
        openAtt={row.threeNoncsOpenAtt}
        lightMade={row.threeNoncsLightMade}
        lightAtt={row.threeNoncsLightAtt}
        heavyMade={row.threeNoncsHeavyMade}
        heavyAtt={row.threeNoncsHeavyAtt}
        shotType="pullUpThree"
      />
    ),
  },
  {
    field: "threeCs",
    headerName: "C&S 3P",
    width: 160,
    renderHeader: () => <ShotHeader label="C&S 3" />,
    renderCell: ({ row }) => (
      <ShotProfileCell
        openMade={row.threeCsOpenMade}
        openAtt={row.threeCsOpenAtt}
        lightMade={row.threeCsLightMade}
        lightAtt={row.threeCsLightAtt}
        heavyMade={row.threeCsHeavyMade}
        heavyAtt={row.threeCsHeavyAtt}
        shotType="csThree"
      />
    ),
  },
];

export const dashboardColumnGroups: GridColumnGroupingModel = [
  {
    groupId: "Rebounding",
    children: [{ field: "threePtCrashes" }],
  },
  {
    groupId: "Passing",
    children: [{ field: "hitAheadPasses" }, { field: "paintTouches" }, { field: "potentialAssists" }],
  },
  {
    groupId: "Shooting",
    children: [{ field: "rim" }, { field: "nonRimTwo" }, { field: "threeNoncs" }, { field: "threeCs" }],
  },
];
